import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiErrorDetail } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';

const PrizeManagement = () => {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingPrize, setEditingPrize] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    quantity: 1,
    display_order: 0,
    is_active: true,
  });

  useEffect(() => {
    loadPrizes();
  }, []);

  const loadPrizes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/prizes');
      setPrizes(data);
    } catch (error) {
      toast.error('Failed to load prizes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrize = async (e) => {
    e.preventDefault();
    try {
      await api.post('/prizes', formData);
      toast.success('Prize added successfully');
      setShowAddDialog(false);
      resetForm();
      loadPrizes();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to add prize');
    }
  };

  const handleUpdatePrize = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/prizes/${editingPrize.id}`, formData);
      toast.success('Prize updated successfully');
      setEditingPrize(null);
      resetForm();
      loadPrizes();
    } catch (error) {
      toast.error('Failed to update prize');
    }
  };

  const handleDeletePrize = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prize?')) return;

    try {
      await api.delete(`/prizes/${id}`);
      toast.success('Prize deleted successfully');
      loadPrizes();
    } catch (error) {
      toast.error('Failed to delete prize');
    }
  };

  const openEditDialog = (prize) => {
    setEditingPrize(prize);
    setFormData({
      name: prize.name,
      description: prize.description,
      image_url: prize.image_url,
      quantity: prize.quantity,
      display_order: prize.display_order,
      is_active: prize.is_active,
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image_url: '',
      quantity: 1,
      display_order: 0,
      is_active: true,
    });
  };

  return (
    <div className="min-h-screen bg-[#080808] pb-12">
      <div className="bg-[#121212] border-b border-[#D4AF37]/20 px-6 py-4 mb-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-white">Prize Management</h1>
          </div>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8]"
            data-testid="btn-add-prize"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Prize
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="prizes-grid">
          {loading ? (
            <div className="col-span-full text-center text-[#A0A0A0] py-12">Loading prizes...</div>
          ) : prizes.length === 0 ? (
            <div className="col-span-full text-center text-[#A0A0A0] py-12">No prizes found</div>
          ) : (
            prizes.map((prize) => (
              <Card
                key={prize.id}
                className="bg-[#121212] border border-[#D4AF37]/10 rounded-2xl overflow-hidden"
                data-testid={`prize-card-${prize.id}`}
              >
                <div className="aspect-video bg-gradient-to-br from-[#D4AF37]/20 to-[#F3A6C8]/20 relative overflow-hidden">
                  <img src={prize.image_url} alt={prize.name} className="w-full h-full object-cover" />
                  {!prize.is_active && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <span className="text-white font-bold">INACTIVE</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{prize.name}</h3>
                  <p className="text-[#A0A0A0] text-sm mb-4">{prize.description}</p>
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <div className="text-[#A0A0A0]">Quantity</div>
                      <div className="text-white font-semibold">{prize.quantity}</div>
                    </div>
                    <div>
                      <div className="text-[#A0A0A0]">Remaining</div>
                      <div className="text-[#D4AF37] font-semibold">{prize.remaining_quantity}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openEditDialog(prize)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-[#D4AF37]/30 text-[#D4AF37]"
                    >
                      <Edit className="w-3 h-3 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeletePrize(prize.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-500/30 text-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog || !!editingPrize} onOpenChange={() => { setShowAddDialog(false); setEditingPrize(null); }}>
        <DialogContent className="bg-[#121212] border-[#D4AF37]/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPrize ? 'Edit Prize' : 'Add New Prize'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingPrize ? handleUpdatePrize : handleAddPrize} className="space-y-4">
            <div>
              <Label htmlFor="name">Prize Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-[#080808] border-[#D4AF37]/30 text-white"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-[#080808] border-[#D4AF37]/30 text-white"
              />
            </div>
            <div>
              <Label htmlFor="image_url">Image URL *</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                required
                className="bg-[#080808] border-[#D4AF37]/30 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                  required
                  className="bg-[#080808] border-[#D4AF37]/30 text-white"
                />
              </div>
              <div>
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  className="bg-[#080808] border-[#D4AF37]/30 text-white"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            <Button
              type="submit"
              className="w-full bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8]"
              data-testid="btn-submit-prize"
            >
              {editingPrize ? 'Update Prize' : 'Add Prize'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrizeManagement;
